import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Code2, Copy, Check, Terminal, Webhook, KeyRound, Gauge, Lock, Rocket } from 'lucide-react';
import { apiBase } from '../lib/site';

function useEndpoints() {
  const api = apiBase();
  const wss = api.replace(/^https/, 'wss');
  return useMemo(() => ([
    {
      method: 'GET', path: '/v1/trades',
      desc: 'List your trades. Paginated. Filter by status, date range, wallet.',
      example: `curl -H "Authorization: Bearer $PFS_KEY" \\\n  "${api}/v1/trades?limit=50&status=filled"`,
      response: `{
  "data": [
    {
      "id": "trd_2n8s9HxKL",
      "token": "MOON",
      "side": "buy",
      "amount_sol": 0.42,
      "price_sol": 0.000018,
      "pnl_percent": 238.4,
      "status": "filled",
      "executed_at": "2026-01-14T09:12:44Z"
    }
  ],
  "next": "cursor_xkf82"
}`,
    },
    {
      method: 'POST', path: '/v1/snipe',
      desc: 'Submit a manual snipe with custom rules. Returns an order id.',
      example: `curl -X POST ${api}/v1/snipe \\\n  -H "Authorization: Bearer $PFS_KEY" \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "token_mint": "CzLSujWBLFsSjncfkh59rUFqvafWcY5tzedWJSuypump",\n    "amount_sol": 0.2,\n    "slippage": 5,\n    "take_profit": 50,\n    "stop_loss": 25\n  }'`,
      response: `{
  "id": "snp_9F2KqJxL",
  "status": "pending",
  "eta_ms": 12
}`,
    },
    {
      method: 'GET', path: '/v1/wallets',
      desc: 'List connected wallets and current balances.',
      example: `curl -H "Authorization: Bearer $PFS_KEY" \\\n  ${api}/v1/wallets`,
      response: `{
  "data": [
    { "address": "7xKXtg2...asU", "balance_sol": 4.82, "is_trading": true }
  ]
}`,
    },
    {
      method: 'POST', path: '/v1/config',
      desc: 'Update bot configuration programmatically.',
      example: `curl -X POST ${api}/v1/config \\\n  -H "Authorization: Bearer $PFS_KEY" \\\n  -d '{"slippage":5,"take_profit":50,"rugpull_shield":"v3"}'`,
      response: `{ "updated": true }`,
    },
    {
      method: 'WS', path: '/v1/stream',
      desc: 'WebSocket stream of live snipes, fills and alerts.',
      example: `wscat -c ${wss}/v1/stream \\\n  -H "Authorization: Bearer $PFS_KEY"`,
      response: `event: trade_filled
data: { "id":"trd_2n8s9HxKL", "token":"MOON", "pnl":"+238%" }`,
    },
  ]), [api, wss]);
}

const RATE_LIMITS = [
  { plan: 'Starter', rps: '—',   day: '—',         note: 'No API access' },
  { plan: 'Pro',     rps: '50',  day: '10k',       note: 'Included' },
  { plan: 'Whale',   rps: '500', day: 'Unlimited', note: 'Fair-use 200 rps sustained' },
];

function CodeBlock({ children, label }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(children);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="relative min-w-0 max-w-full">
      {label && <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5 font-mono">{label}</div>}
      <pre className="p-4 rounded-lg bg-black/60 border border-border/70 font-mono text-xs text-[hsl(var(--primary))] overflow-x-auto max-w-full"><code>{children}</code></pre>
      <button onClick={copy}
        className="absolute top-2 right-2 p-1.5 rounded-md bg-background/70 border border-border/70 hover:border-[hsl(var(--primary))]/60 text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors">
        {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
      </button>
    </div>
  );
}

const methodColor = (m) => ({
  GET: 'bg-sky-500/15 text-sky-400 border-sky-500/40',
  POST: 'bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/40',
  DELETE: 'bg-red-500/15 text-red-400 border-red-500/40',
  WS: 'bg-purple-500/15 text-purple-400 border-purple-500/40',
}[m] || '');

export default function ApiPage() {
  const ENDPOINTS = useEndpoints();
  const api = apiBase();
  return (
    <div className="pt-10 sm:pt-14 pb-14 sm:pb-20 container-x" data-testid="api-page">
      <section className="text-center py-8 sm:py-10">
        <span className="chip"><Code2 className="w-3 h-3" /> API Reference</span>
        <h1 className="font-display font-black text-3xl sm:text-5xl lg:text-6xl tracking-tighter mt-4">
          Build on top of the <span className="gradient-text">sniper</span>
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base mt-3 sm:mt-4 max-w-xl mx-auto">
          REST &amp; WebSocket. JSON everywhere. Full docs below.
        </p>
      </section>

      <div className="glass-card p-4 sm:p-6 mt-2 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 border-[hsl(var(--primary))]/30" data-testid="api-pro-notice">
        <div className="w-11 h-11 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/40 flex items-center justify-center text-[hsl(var(--primary))] shrink-0">
          <Lock className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-base">API access requires Pro or Whale</div>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">
            The REST &amp; WebSocket API is a Pro+ feature.
            The reference below is public; generating a live <span className="font-mono text-[hsl(var(--primary))]">pfs_live_*</span> key requires an active Pro or Whale subscription.
          </p>
        </div>
        <a href="#/pricing" className="btn-primary shrink-0 w-full sm:w-auto justify-center"><Rocket className="w-4 h-4" /> Upgrade</a>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mt-8 sm:mt-10">
        {[
          { Icon: KeyRound, t: 'Authentication', d: 'Bearer token in the `Authorization` header. Keys created from the terminal settings.' },
          { Icon: Gauge,    t: 'Rate limits',   d: 'Depends on plan (see below). 429 on excess with Retry-After.' },
          { Icon: Webhook,  t: 'Webhooks',      d: 'Signed with HMAC-SHA256. 5 retries with exponential backoff.' },
        ].map((b) => (
          <div key={b.t} className="glass-card p-4 sm:p-5">
            <div className="w-10 h-10 rounded-xl bg-[hsl(var(--primary))]/10 border border-[hsl(var(--primary))]/30 flex items-center justify-center text-[hsl(var(--primary))]">
              <b.Icon className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-base mt-3">{b.t}</h3>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 leading-relaxed">{b.d}</p>
          </div>
        ))}
      </div>

      <div className="glass-card p-4 sm:p-6 mt-8 sm:mt-10">
        <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Terminal className="w-4 h-4 text-[hsl(var(--primary))]" /> Quick start</h3>
        <CodeBlock label="bash">{`export PFS_KEY="pfs_live_xxx..."
curl -H "Authorization: Bearer $PFS_KEY" \\\n  ${api}/v1/wallets`}</CodeBlock>
      </div>

      <h2 className="font-display font-black text-2xl sm:text-3xl mt-10 sm:mt-12 mb-4 sm:mb-5 tracking-tight">Endpoints</h2>
      <div className="space-y-4 sm:space-y-5">
        {ENDPOINTS.map((e, i) => (
          <motion.div
            key={e.path + i}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4 }}
            className="glass-card p-4 sm:p-6"
          >
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-2.5 py-1 rounded-md text-xs font-mono font-bold border ${methodColor(e.method)}`}>{e.method}</span>
              <span className="font-mono font-semibold text-sm sm:text-base break-all">{e.path}</span>
            </div>
            <p className="text-xs sm:text-sm text-muted-foreground mt-2">{e.desc}</p>
            <div className="grid lg:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-5 min-w-0">
              <CodeBlock label="request">{e.example}</CodeBlock>
              <CodeBlock label="response">{e.response}</CodeBlock>
            </div>
          </motion.div>
        ))}
      </div>

      <h2 className="font-display font-black text-2xl sm:text-3xl mt-10 sm:mt-12 mb-4 sm:mb-5 tracking-tight">Rate limits</h2>
      <div className="glass-card overflow-x-auto">
        <table className="w-full text-xs sm:text-sm min-w-[480px]">
          <thead>
            <tr className="text-left bg-black/30 border-b border-border/60 font-mono text-[10px] sm:text-xs uppercase tracking-wider text-muted-foreground">
              <th className="px-4 sm:px-5 py-3 whitespace-nowrap">Plan</th>
              <th className="px-4 sm:px-5 py-3 whitespace-nowrap">Req / sec</th>
              <th className="px-4 sm:px-5 py-3 whitespace-nowrap">Req / day</th>
              <th className="px-4 sm:px-5 py-3 whitespace-nowrap">Note</th>
            </tr>
          </thead>
          <tbody>
            {RATE_LIMITS.map((r) => (
              <tr key={r.plan} className="border-b border-border/40 last:border-0">
                <td className="px-4 sm:px-5 py-3 font-semibold whitespace-nowrap">{r.plan}</td>
                <td className="px-4 sm:px-5 py-3 font-mono text-[hsl(var(--primary))] whitespace-nowrap">{r.rps}</td>
                <td className="px-4 sm:px-5 py-3 font-mono whitespace-nowrap">{r.day}</td>
                <td className="px-4 sm:px-5 py-3 text-muted-foreground">{r.note}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
